/**
 * Encontra sobreposição de férias do mesmo time (por cliente e responsável) com base numa solicitação em análise.
 *
 * Diferente de `findTeamVacationOverlaps` que só conta períodos aprovados (Scheduled/InProgress/Completed),
 * esta função compara a solicitação sendo decidida (que está em PendingDP) contra os períodos dos colegas,
 * incluindo pendentes. Útil para o modal de decisão do DP.
 *
 * Nota: "time" é definido pelos `clientNames` e `clientResponsibles` vindos de `allocationSummary.active[]` no Firestore.
 * Não é squad/departamento nem a alocação vigente na data das férias.
 */

import type { VacationDpRow } from '../models/vacationDpBoard.types';
import type { VacationSegment } from '../models/vacation.types';
import { createDateSafe } from '@/utils/dateUtils';

const toDay = (value: string | Date): Date =>
    value instanceof Date ? value : (createDateSafe(value) ?? new Date(NaN));
import { periodOverlap, overlapRange, isNearby, TEAM_PROXIMITY_DAYS, getClientResponsibleKeys } from './vacationTeamOverlap';

export interface RequestTeamOverlap {
  collaboratorId: string;
  collaboratorName: string;
  proximity: 'overlap' | 'nearby';
  overlapDays: number;
  overlapStart: Date | null;
  overlapEnd: Date | null;
  gapDays: number;
  requestStatus: string; // Status da solicitação deles (para contexto)
}

/**
 * Compara os segmentos de uma solicitação contra os períodos dos colegas do mesmo cliente.
 *
 * @param request - A solicitação sendo decidida (em PendingDP)
 * @param allRows - Todas as rows do painel do DP (com dados enriquecidos)
 * @returns Array de sobreposições com colegas, ou vazio se nenhuma
 */
export function findRequestTeamOverlaps(
  request: VacationDpRow['request'] | undefined,
  allRows: VacationDpRow[]
): RequestTeamOverlap[] {
  if (!request || !request.segments || request.segments.length === 0) {
    return [];
  }

  const overlaps: RequestTeamOverlap[] = [];

  // Extrair nomes dos clientes da solicitação
  // A solicitação pode ter múltiplos segmentos, mas todos num único período.
  // Porém, não temos `clientName` direto na solicitação — vem do `allocationSummary` do colaborador.
  // Usaremos os `clientNames` das rows que têm dados dele, ou procuramos no contexto de `allRows`.

  // Buscar o próprio row para pegar seus clientes
  const myRow = allRows.find((r) => r.collaboratorId === request.collaboratorId);
  if (!myRow) {
    return []; // Sem contexto próprio, não conseguimos agrupar
  }

  const myClientNames = myRow.clientNames ?? [];
  if (myClientNames.length === 0) {
    return []; // Sem alocação, sem time
  }

  const colleagueRows = allRows.filter((colleagueRow) => {
    if (colleagueRow.collaboratorId === request.collaboratorId) return false;

    const colleagueClientNames = colleagueRow.clientNames ?? [];
    const commonClients = myClientNames.filter((clientName) =>
      colleagueClientNames.some((c) => c.toLowerCase().trim() === clientName.toLowerCase().trim())
    );

    if (commonClients.length === 0) return false;

    for (const commonClient of commonClients) {
      const normalizedClientName = commonClient.toLowerCase().trim();
      const myResponsibleKeys = getClientResponsibleKeys(myRow, normalizedClientName);
      const colleagueResponsibleKeys = getClientResponsibleKeys(colleagueRow, normalizedClientName);

      const hasCommonResponsible = Array.from(myResponsibleKeys).some((key) =>
        colleagueResponsibleKeys.has(key)
      );

      if (hasCommonResponsible) return true;
    }

    return false;
  });

  // Converter segmentos para Date para comparação
  const requestSegments = request.segments.map((seg) => {
    const start = toDay(seg.startDate);
    const end = toDay(seg.endDate);
    return { start, end };
  });

  // Comparar contra cada colega
  for (const colleagueRow of colleagueRows) {
    // Incluir tanto aprovados quanto pendentes, deduplicando por período
    const allPeriods = [
      ...(colleagueRow.approvedPeriods ?? []),
      ...((colleagueRow.request?.segments ?? []).map((seg) => ({
        startDate: seg.startDate,
        endDate: seg.endDate,
      })) ?? []),
    ];

    // Deduplicar períodos identicos (colega com solicitação já aprovada apareceria duas vezes)
    const uniquePeriods = Array.from(
      new Map(
        allPeriods.map((p) => [
          `${new Date(p.startDate).getTime()}_${new Date(p.endDate).getTime()}`,
          p,
        ])
      ).values()
    );

    for (const period of uniquePeriods) {
      const colleagueStart = toDay(period.startDate);
      const colleagueEnd = toDay(period.endDate);

      // Checar sobreposição contra cada segmento meu
      for (const mySegment of requestSegments) {
        const overlap = periodOverlap(
          mySegment.start,
          mySegment.end,
          colleagueStart,
          colleagueEnd
        );

        if (overlap > 0) {
          // Há sobreposição
          const range = overlapRange(mySegment.start, mySegment.end, colleagueStart, colleagueEnd);
          overlaps.push({
            collaboratorId: colleagueRow.collaboratorId,
            collaboratorName: colleagueRow.collaboratorName,
            proximity: 'overlap',
            overlapDays: overlap,
            overlapStart: range?.start ?? null,
            overlapEnd: range?.end ?? null,
            gapDays: 0,
            requestStatus: colleagueRow.request?.status ?? 'Unknown',
          });
        } else if (
          isNearby(mySegment.end, colleagueStart, TEAM_PROXIMITY_DAYS) ||
          isNearby(colleagueEnd, mySegment.start, TEAM_PROXIMITY_DAYS)
        ) {
          // Há proximidade (mesmos 7 dias)
          const gapDays =
            colleagueStart < mySegment.start
              ? Math.ceil((mySegment.start.getTime() - colleagueEnd.getTime()) / (1000 * 60 * 60 * 24))
              : Math.ceil((colleagueStart.getTime() - mySegment.end.getTime()) / (1000 * 60 * 60 * 24));

          overlaps.push({
            collaboratorId: colleagueRow.collaboratorId,
            collaboratorName: colleagueRow.collaboratorName,
            proximity: 'nearby',
            overlapDays: 0,
            overlapStart: null,
            overlapEnd: null,
            gapDays,
            requestStatus: colleagueRow.request?.status ?? 'Unknown',
          });
        }
      }
    }
  }

  // Remover duplicatas (pode haver múltiplas sobreposições entre a mesma dupla)
  const seen = new Set<string>();
  return overlaps.filter((o) => {
    const key = `${o.collaboratorId}_${o.proximity}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
